/**
 * IVR Selection — the Analytics view.
 *
 * The table lists the keypresses; this answers the menu-design questions:
 * which options callers actually want, how long they listen before choosing,
 * how often a menu is a dead end, and how many callers give up inside it.
 *
 * "Chose nothing" only appears when the drawer asked for those visits — the
 * report excludes them by default, and a zero drawn from a filtered-out
 * population would read as "nobody abandons".
 */
import { useMemo, type JSX } from 'react'
import { Chart } from 'primereact/chart'
import {
  CalendarClock, Clock, Hash, ListTree, LogOut, PhoneOff, Route,
} from 'lucide-react'

import {
  AnalyticsCanvas,
  BarList,
  CardEmpty,
  CardGrid,
  ChartCard,
  Heatmap,
  HeroBand,
  KpiRow,
  Meter,
  SectionLabel,
  StatTile,
  type BarListItem,
} from '../analytics/AnalyticsKit.js'
import {
  VIZ,
  WEEKDAYS,
  formatCount,
  formatPct,
  formatSeconds,
  hourDayMatrix,
  hourLabel,
  lineDataset,
  lineOptions,
  medianOf,
  ratePct,
  tally,
} from '../analytics/viz.js'
import {
  avgCol,
  bucketLabel,
  bucketSeries,
  num,
  peakOf,
  text,
} from './analytics-helpers.js'

type Row = Record<string, unknown>

export default function IvrSelectionAnalytics({ rows }: { rows: readonly Row[] }): JSX.Element {
  const series = useMemo(() => bucketSeries(rows, 'bucket'), [rows])
  const busiest = useMemo(() => peakOf(series), [series])
  const heat = useMemo(() => hourDayMatrix(rows, 'pressedAt'), [rows])

  const trendData = useMemo(
    () => ({
      labels: series.labels,
      datasets: [lineDataset('Selections', series.values, VIZ.series[0], { fill: true })],
    }),
    [series],
  )

  const totals = useMemo(() => {
    // Counted per SESSION, not per row: a caller who pressed three keys in one
    // visit abandoned at most once, and counting the rows would treat a deep
    // menu as three separate callers.
    const visits = new Map<string, { abandoned: boolean; presses: number; deadEnd: boolean }>()
    let pressed = 0
    let deadEnds = 0
    for (const row of rows) {
      const key = `${text(row['uniqueId'])}|${text(row['ivrEnteredAt'])}`
      const entry = visits.get(key) ?? { abandoned: false, presses: 0, deadEnd: false }
      if (text(row['exitReason']) === 'hangup') entry.abandoned = true
      if (text(row['digit']) !== '') {
        entry.presses += 1
        pressed += 1
        // A keypress with no label is an option the menu never announced —
        // the caller reached for something that is not on offer.
        if (text(row['optionLabel']) === '') {
          entry.deadEnd = true
          deadEnds += 1
        }
      }
      visits.set(key, entry)
    }
    const list = [...visits.values()]
    return {
      visits: list.length,
      abandoned: list.filter((v) => v.abandoned).length,
      noSelection: list.filter((v) => v.presses === 0).length,
      multiPress: list.filter((v) => v.presses > 1).length,
      pressed,
      deadEnds,
    }
  }, [rows])

  const avgTimeToPress = useMemo(() => avgCol(rows, 'timeToPressSec'), [rows])
  const medianTimeToPress = useMemo(
    () => medianOf(rows.map((r) => num(r['timeToPressSec']))),
    [rows],
  )
  const avgIvrTime = useMemo(() => avgCol(rows, 'ivrSec'), [rows])

  const toItems = (key: string, limit = 8, emptyLabel = 'Not recorded'): BarListItem[] =>
    tally(rows, key, { limit, emptyLabel }).map((slice) => ({
      label: slice.label,
      value: slice.value,
      meta: formatPct(ratePct(slice.value, rows.length), 1),
    }))

  const options = useMemo(() => toItems('optionLabel', 10, 'Unlabelled option'), [rows])
  const digits = useMemo(() => toItems('digit', 12, 'No keypress'), [rows])
  const menus = useMemo(() => toItems('ivrName'), [rows])
  const routedTo = useMemo(() => toItems('ivrDestinationType', 8, 'Not routed'), [rows])
  const dids = useMemo(() => toItems('did'), [rows])

  const exits = useMemo(() => {
    const counts = { selection: 0, hangup: 0, timeout: 0, invalid: 0, failure: 0 }
    for (const row of rows) {
      const reason = text(row['exitReason'])
      if (reason in counts) counts[reason as keyof typeof counts] += 1
    }
    return counts
  }, [rows])

  if (rows.length === 0) {
    return (
      <AnalyticsCanvas>
        <div className="rounded-[4px] border border-slate-200 bg-white px-6 py-16 text-center">
          <p className="text-sm font-semibold text-slate-900">Nothing to analyse</p>
          <p className="mt-1 text-xs text-slate-500">
            No caller reached an IVR menu in this window. Widen the range, or clear the IVR
            filters, then run the report again.
          </p>
        </div>
      </AnalyticsCanvas>
    )
  }

  const peak = heat.peak
  const abandonRate = ratePct(totals.abandoned, totals.visits)

  return (
    <AnalyticsCanvas>
      <HeroBand
        eyebrow="IVR selections"
        value={formatCount(totals.pressed)}
        caption={`Across ${formatCount(totals.visits)} menu visit${totals.visits === 1 ? '' : 's'} in the selected window`}
        spark={series.values}
        Icon={ListTree}
        facts={[
          { label: 'Menu visits', value: formatCount(totals.visits) },
          {
            label: 'Abandoned in menu',
            value: formatPct(abandonRate, 1),
            tone: (abandonRate ?? 0) > 5 ? 'critical' : 'good',
          },
          { label: 'Median time to press', value: formatSeconds(medianTimeToPress) },
          { label: 'Average time in menu', value: formatSeconds(avgIvrTime) },
        ]}
      />

      <KpiRow>
        <StatTile label="Keys pressed" value={formatCount(totals.pressed)} />
        <StatTile
          label="Abandoned in menu"
          value={formatCount(totals.abandoned)}
          tone={totals.abandoned > 0 ? 'critical' : 'good'}
          hint="Caller hung up before being routed"
        />
        <StatTile
          label="Chose nothing"
          value={formatCount(totals.noSelection)}
          tone={totals.noSelection > 0 ? 'warning' : 'default'}
          hint="Visits with no keypress at all"
        />
        <StatTile
          label="Needed more than one key"
          value={formatCount(totals.multiPress)}
          hint="Nested menus the caller had to work through"
        />
        <StatTile
          label="Unoffered keys"
          value={formatCount(totals.deadEnds)}
          tone={totals.deadEnds > 0 ? 'warning' : 'good'}
          hint="Presses the menu had no option for"
        />
        <StatTile label="Average time to press" value={formatSeconds(avgTimeToPress)} />
      </KpiRow>

      {/* ── What callers choose ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel>What callers ask for</SectionLabel>
        <CardGrid>
          <ChartCard title="Options chosen" subtitle="By the menu's own wording" Icon={Route} wide>
            <BarList items={options} format={formatCount} emptyMessage="No labelled option was chosen." />
          </ChartCard>
          <ChartCard title="Keys pressed" subtitle="Raw DTMF, including keys with no option" Icon={Hash}>
            <BarList items={digits} format={formatCount} emptyMessage="No keypress in this window." />
          </ChartCard>
          <ChartCard title="Menus" subtitle="Which IVR callers landed in" Icon={ListTree}>
            <BarList items={menus} format={formatCount} emptyMessage="No IVR recorded." />
          </ChartCard>
        </CardGrid>
      </section>

      {/* ── How the visit ends ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel>How the visit ends</SectionLabel>
        <CardGrid>
          <ChartCard title="Exit reason" subtitle="Routed, gave up, or the menu gave out" height={220} Icon={LogOut}>
            <Meter
              segments={[
                { label: 'Routed', value: exits.selection, color: VIZ.series[0] },
                // The two outcomes that mean something went wrong take the
                // status colors rather than a fourth and fifth categorical
                // hue — the palette has three slots, and severity should not
                // be read as just another category anyway.
                { label: 'Hung up', value: exits.hangup, color: VIZ.status.critical },
                { label: 'Timed out', value: exits.timeout, color: VIZ.series[1] },
                { label: 'Invalid', value: exits.invalid, color: VIZ.series[2] },
                { label: 'Failed', value: exits.failure, color: VIZ.status.warning },
              ]}
              emptyMessage="No visit in this window has finished."
            />
          </ChartCard>
          <ChartCard title="Where the menu sent them" subtitle="Destination type" Icon={Route}>
            <BarList items={routedTo} format={formatCount} emptyMessage="No visit was routed onwards." />
          </ChartCard>
          <ChartCard title="Numbers dialled in on" subtitle="Inbound numbers reaching the menu" Icon={PhoneOff}>
            <BarList items={dids} format={formatCount} emptyMessage="No DID recorded." />
          </ChartCard>
        </CardGrid>
      </section>

      {/* ── When ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel note={`${series.labels.length} interval${series.labels.length === 1 ? '' : 's'}`}>
          When callers use the menu
        </SectionLabel>
        <CardGrid>
          <ChartCard
            title="Selections per interval"
            subtitle="On the interval chosen in the filters"
            Icon={CalendarClock}
            wide
            height={280}
            footnote={
              busiest
                ? `Busiest interval: ${bucketLabel(busiest.label)} with ${formatCount(busiest.value)} selections.`
                : undefined
            }
          >
            {series.labels.length === 0 ? (
              <CardEmpty message="No row in this result set carries an interval." />
            ) : (
              <Chart
                type="line"
                data={trendData}
                options={lineOptions({ tickLabel: bucketLabel })}
                className="h-full w-full"
              />
            )}
          </ChartCard>
          <ChartCard
            title="When selections happen"
            subtitle="Weekday by hour of day, in your local time"
            Icon={Clock}
            wide
            height={280}
            footnote={
              peak
                ? `Peak hour: ${WEEKDAYS[peak.day]} at ${hourLabel(peak.hour)} — ${formatCount(peak.value)} selections.`
                : undefined
            }
          >
            <Heatmap matrix={heat.matrix} max={heat.max} />
          </ChartCard>
        </CardGrid>
      </section>
    </AnalyticsCanvas>
  )
}
